/**
 * FirmwareUpdateNotificationService
 *
 * Service to manage firmware update notifications for LG WebOS Player displays.
 * Handles both single display and batch update notifications.
 */
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface FirmwareUpdateNotification {
  id: string;
  type: 'single' | 'batch';
  displayName?: string;
  displayCount?: number;
  timestamp: Date;
}

@Injectable({
  providedIn: 'root',
})
export class FirmwareUpdateNotificationService {
  private readonly notificationsSubject = new BehaviorSubject<
    FirmwareUpdateNotification[]
  >([]);
  public notifications$ = this.notificationsSubject.asObservable();

  constructor() {
    // Service initialized
  }

  /**
   * Add a single display firmware update notification
   */
  addSingleUpdateNotification(displayName: string): void {
    const notification: FirmwareUpdateNotification = {
      id: this.generateId(),
      type: 'single',
      displayName,
      timestamp: new Date(),
    };

    this.addNotification(notification);
  }

  /**
   * Add a batch firmware update notification
   */
  addBatchUpdateNotification(displayCount: number): void {
    const notification: FirmwareUpdateNotification = {
      id: this.generateId(),
      type: 'batch',
      displayCount,
      timestamp: new Date(),
    };

    this.addNotification(notification);
  }

  /**
   * Remove a specific notification
   */
  removeNotification(notificationId: string): void {
    const currentNotifications = this.notificationsSubject.value;
    const updatedNotifications = currentNotifications.filter(
      (n) => n.id !== notificationId,
    );
    this.notificationsSubject.next(updatedNotifications);
  }

  /**
   * Clear all notifications
   */
  clearAllNotifications(): void {
    this.notificationsSubject.next([]);
  }

  /**
   * Get the current notifications
   */
  getCurrentNotifications(): FirmwareUpdateNotification[] {
    return this.notificationsSubject.value;
  }

  /**
   * Generate a unique ID for notifications
   */
  private generateId(): string {
    return `firmware-update-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * Add a notification to the list
   */
  private addNotification(notification: FirmwareUpdateNotification): void {
    const currentNotifications = this.notificationsSubject.value;
    this.notificationsSubject.next([...currentNotifications, notification]);
  }
}
